// File: src/components/molecules/ToastContainer.js
// Purpose: Renders the global toast stack via a portal. Mount once near the
// app root. Subscribes to the singleton toast store in utils/toast.js.

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { subscribe, dismissToast } from '../../utils/toast';

const TYPE_STYLES = {
  success: {
    container: 'bg-green-50 border-green-200 text-green-800',
    icon: 'text-green-500',
    role: 'status',
    label: 'Success',
  },
  error: {
    container: 'bg-red-50 border-red-200 text-red-800',
    icon: 'text-red-500',
    role: 'alert',
    label: 'Error',
  },
  warning: {
    container: 'bg-amber-50 border-amber-200 text-amber-800',
    icon: 'text-amber-500',
    role: 'alert',
    label: 'Warning',
  },
  info: {
    container: 'bg-indigo-50 border-indigo-200 text-indigo-800',
    icon: 'text-indigo-500',
    role: 'status',
    label: 'Notice',
  },
};

const ICON_PATHS = {
  success: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  error: 'M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  warning: 'M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z',
  info: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
};

const ToastItem = ({ toast, onDismiss }) => {
  const styles = TYPE_STYLES[toast.type] || TYPE_STYLES.info;
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      role={styles.role}
      className={`pointer-events-auto flex items-start gap-3 w-full max-w-sm rounded-lg border shadow-lg px-4 py-3 transition-all duration-300 ease-out ${
        styles.container
      } ${entered ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'}`}
    >
      <svg
        className={`h-5 w-5 flex-shrink-0 mt-0.5 ${styles.icon}`}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d={ICON_PATHS[toast.type] || ICON_PATHS.info} />
      </svg>
      <p className="flex-1 text-sm font-medium leading-5 break-words">{toast.message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification"
        className="flex-shrink-0 -mr-1 -mt-0.5 rounded p-1 opacity-60 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-current"
      >
        <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);

  useEffect(() => subscribe(setToasts), []);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed top-4 right-4 z-[9999] flex flex-col items-end gap-2"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => dismissToast(t.id)} />
      ))}
    </div>,
    document.body
  );
};

export default ToastContainer;
